import { Check, Database, Hourglass, House, Receipt, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import heroImage from "@/images/site/home-hero.jpg";
import rollingStoneLogoImg from "@/images/rslogo.png";
import forbesLogoImg from "@/images/forbes.png";
import msnLogoImg from "@/images/msn-logo.svg";
import { cn } from "@/lib/utils";
import Container from "./site/Container";
import CtaBand from "./site/CtaBand";
import CtaLink from "./site/CtaLink";
import Eyebrow from "./site/Eyebrow";
import { GET_STARTED_PATH } from "./site/nav";

interface Option {
  icon: LucideIcon;
  title: string;
  text: string;
  solvesIt: boolean;
}

const options: Option[] = [
  {
    icon: Database,
    title: "Smaller deposit?",
    text: "A smaller deposit doesn’t solve the shortfall to the home you want.",
    solvesIt: false,
  },
  {
    icon: Hourglass,
    title: "Save a bigger deposit?",
    text: "Waiting and saving more doesn’t stop home prices from rising.",
    solvesIt: false,
  },
  {
    icon: Receipt,
    title: "Another mortgage?",
    text: "A second mortgage still leaves a significant affordability gap.",
    solvesIt: false,
  },
  {
    icon: House,
    title: "BuySooner Boost",
    text: "A larger buying budget so you can buy the home you want, sooner.",
    solvesIt: true,
  },
];

const OptionCard = ({ icon: Icon, title, text, solvesIt }: Option) => {
  return (
    <li className="relative rounded-2xl bg-white p-6 pt-9 shadow-[0_12px_40px_-16px_rgba(3,35,58,0.22)] ring-1 ring-slate-900/5">
      <span
        className={cn(
          "absolute -top-3.5 left-5 grid size-8 place-items-center rounded-full text-white ring-4 ring-white",
          solvesIt ? "bg-green-600" : "bg-red-500",
        )}
      >
        {solvesIt ? (
          <Check className="size-4" strokeWidth={3} aria-hidden />
        ) : (
          <X className="size-4" strokeWidth={3} aria-hidden />
        )}
        <span className="sr-only">{solvesIt ? "Yes:" : "No:"}</span>
      </span>
      <Icon
        className={cn("mx-auto size-11", solvesIt ? "text-brand-ink" : "text-navy")}
        strokeWidth={1.5}
        aria-hidden
      />
      <h3 className={cn("mt-6 text-[17px] font-semibold", solvesIt ? "text-brand-ink-strong" : "text-navy")}>
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
    </li>
  );
};

const Home = () => {
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 hidden w-[52%] lg:block">
          <img
            src={heroImage}
            alt="A family standing in front of a modern home"
            className="h-full w-full object-cover xl:object-[25%_center]"
          />
          <div className="absolute inset-y-0 left-0 w-3/5 bg-linear-to-r from-white via-white/70 to-transparent xl:w-[30%] xl:via-white/50" />
        </div>
        <Container className="relative">
          <div className="max-w-2xl py-14 sm:py-20 lg:py-24">
            <Eyebrow>A bigger tomorrow starts today</Eyebrow>
            <h1 className="mt-5 text-[2.6rem] leading-[1.08] font-bold tracking-tight sm:text-6xl lg:text-[4rem]">
              <span className="block text-navy">
                You don’t need <br className="hidden sm:block" />a smaller dream.
              </span>
              <span className="block text-brand-ink">
                You need a bigger <br className="hidden sm:block" />
                <span className="whitespace-nowrap">home-buying</span> budget.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Most Australians face a shortfall between what they can afford and the home they actually want. A smaller
              deposit doesn’t solve that. Neither does another mortgage. And waiting to save more doesn’t stop home
              prices from moving.
            </p>
            <p className="mt-3 text-lg font-semibold text-navy">You need a Boost to BuySooner.</p>
            <CtaLink to={GET_STARTED_PATH} className="mt-8">
              Help me BuySooner
            </CtaLink>
          </div>
          <img
            src={heroImage}
            alt=""
            className="mb-6 aspect-[4/3] w-full rounded-2xl object-cover object-[center_70%] lg:hidden"
          />
        </Container>
      </section>

      <section className="pt-8 pb-20">
        <Container>
          <ul className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {options.map((option) => (
              <OptionCard key={option.title} {...option} />
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-t border-slate-100 py-14">
        <Container className="flex flex-col items-center gap-6">
          <p className="text-xs font-semibold tracking-[0.14em] text-slate-500 uppercase">As seen in</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 opacity-70 grayscale">
            <img src={forbesLogoImg} alt="Forbes" className="h-7 object-contain md:h-9" />
            <img src={rollingStoneLogoImg} alt="Rolling Stone" className="h-20 object-contain md:h-28" />
            <img src={msnLogoImg} alt="MSN" className="h-8 object-contain md:h-11" />
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
};

export default Home;
